<template>
  <div >

        <section >
                <div class="">
                    <!-- Mobile action buttons -->
                    <div class="d-flex d-md-none gap-2 mb-3" v-if="!isDetailSupplier">
                        <button @click="create" v-if="helper.checkAccess('create_payments') != -1" class="btn btn-sm btn-success flex-grow-1">
                            <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                            <span>{{$t('payment')}}</span>
                        </button>
                    </div>

                    <!-- Filters section -->
                    <div class="row align-items-end g-2 mb-4" v-if="!isDetailSupplier" :class="{'d-none d-md-flex': !showFilters}">
                        
                        <!-- Filter Dropdown (Shopify Style) -->
                        <div class="col-12 col-md-auto">
                            <div class="dropdown">
                                <button class="btn btn-outline-secondary dropdown-toggle btn-sm" type="button" id="paymentFilterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                                    <AppIcon name="bi-funnel" class="bi bi-funnel" /> {{$t('filter')}}
                                    <span v-if="activeFilters.length" class="badge bg-secondary ms-1">{{activeFilters.length}}</span>
                                </button>
                                <button v-if="activeFilters.length" class="btn btn-link btn-sm text-danger text-decoration-none ms-2" type="button" @click.stop="clearAllFilters">
                                    <AppIcon name="bi-x-circle" class="bi bi-x-circle me-1" />{{$t('clear_all')}}
                                </button>
                                <div class="dropdown-menu p-3" aria-labelledby="paymentFilterDropdown" style="min-width: 300px;" @click.stop>
                                    
                                    <!-- Active Filters -->
                                    <div v-if="activeFilters.length > 0" class="mb-3">
                                        <div v-for="(filter, index) in activeFilters" :key="index" class="mb-2">
                                            <label class="form-label small mb-1">{{$t(filter.key)}}</label>
                                            <div v-if="filter.key === 'client'" class="d-flex align-items-center gap-1">
                                                <div class="flex-grow-1">
                                                    <auto-complete
                                                        entity="clients"
                                                        emitEvent="emitAutoCompleteClientFilter"
                                                        :defaultValue="clientFilterName"
                                                        @emitAutoCompleteClientFilter="emitAutoCompleteClientFilter"
                                                    ></auto-complete>
                                                </div>
                                                <button class="btn btn-outline-danger btn-sm" type="button" @click="removeFilter(index)">
                                                    <AppIcon name="bi-trash" class="bi bi-trash" />
                                                </button>
                                            </div>
                                            <div class="input-group input-group-sm" v-else>
                                                <select v-model="filter.value" class="form-select form-select-sm">
                                                    <option v-for="option in getFilterOptions(filter.key)" :key="option.value" :value="option.value">
                                                        {{filter.key === 'category' ? option.label : $t(option.label)}}
                                                    </option>
                                                </select>
                                                <button class="btn btn-outline-danger" type="button" @click="removeFilter(index)">
                                                    <AppIcon name="bi-trash" class="bi bi-trash" />
                                                </button>
                                            </div>
                                        </div>
                                        <hr class="dropdown-divider">
                                    </div>

                                    <!-- Add Filter Options -->
                                    <div class="d-grid gap-2">
                                        <button v-for="filter in selectableFilters" :key="filter.key" 
                                                class="btn btn-sm btn-outline-primary text-start" 
                                                @click="addFilter(filter)">
                                            <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t(filter.key)}}
                                        </button>
                                    </div>

                                    <!-- Apply and Clear -->
                                    <div class="d-flex justify-content-between align-items-center mt-3">
                                        <button class="btn btn-sm btn-success" @click="applyFilters">
                                            {{$t('apply')}}
                                        </button>
                                        <button v-if="activeFilters.length > 0" class="btn btn-link btn-sm text-decoration-none text-danger p-0" @click="clearAllFilters">
                                            {{$t('clear_all')}}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Spacer for desktop -->
                        <div class="col d-none d-md-block"></div>
                        
                        <!-- Date picker -->
                        <div class="col-12 col-md-3 col-lg-2">
                            <label class="form-label small d-md-none mb-1">{{$t('filter_by_date')}}</label>
                            <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm">
                                <option value="">{{$t('filter_by_date')}}</option>
                                <option value="today">{{$t('today')}}</option>
                                <option value="yesterday">{{$t('yesterday')}}</option>
                                <option value="this_week">{{$t('this_week')}}</option>
                                <option value="last_week">{{$t('last_week')}}</option>
                                <option value="this_month">{{$t('this_month')}}</option>
                                <option value="custom">{{$t('custom_date')}}</option>
                            </select>
                        </div>
                        <div class="col-12 col-md-3 col-lg-2" v-if="dateRangeOption === 'custom'">
                            <label class="form-label small d-md-none mb-1">{{$t('custom_date')}}</label>
                            <date-picker :clearable="true" :editable="false" class="w-100" v-model="range" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
                        </div>
                        
                        <!-- Desktop action buttons -->
                        <div class="col-md-2 d-none d-md-block" v-if="helper.checkAccess('create_payments') != -1">
                            <button @click="create" class="btn btn-sm btn-success w-100">
                                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                                <span>{{$t('payment')}}</span>
                            </button>
                        </div>
                    </div>
 
                    <!-- Compact financial summary -->
                    <section v-if="!hidePaymentSummary && (payments.length > 0 || (showFinancialOverview && obligationSummary))" class="payment-summary-panel mb-4">
                        <div class="payment-summary-header">
                            <span class="payment-summary-title">{{$t('payment_summary')}}</span>
                            <span class="payment-summary-scope">
                                <AppIcon name="bi-check-circle" class="bi bi-check-circle me-1" />{{$t('paid_transactions_only')}}
                            </span>
                        </div>

                        <div class="payment-summary-metrics">
                            <div class="payment-summary-metric payment-summary-metric--income">
                                <div>
                                    <span class="payment-summary-label">{{$t('confirmed_cash_inflows')}}</span>
                                    <strong class="payment-summary-value">{{formatMoney(totalEarnings())}}</strong>
                                </div>
                                <AppIcon name="bi-arrow-up-circle" class="bi bi-arrow-up-circle payment-summary-icon" />
                            </div>
                            <div class="payment-summary-metric payment-summary-metric--expense">
                                <div>
                                    <span class="payment-summary-label">{{$t('confirmed_cash_outflows')}}</span>
                                    <strong class="payment-summary-value">{{formatMoney(totalSpending())}}</strong>
                                </div>
                                <AppIcon name="bi-arrow-down-circle" class="bi bi-arrow-down-circle payment-summary-icon" />
                            </div>
                            <div class="payment-summary-metric payment-summary-metric--net">
                                <div>
                                    <span class="payment-summary-label">{{$t('net_cash_flow')}}</span>
                                    <strong class="payment-summary-value" :class="netCashflow() < 0 ? 'text-danger' : ''">{{formatMoney(netCashflow())}}</strong>
                                </div>
                                <AppIcon :name="netCashflow() >= 0 ? 'bi-graph-up' : 'bi-graph-down'" class="bi payment-summary-icon" :class="netCashflow() >= 0 ? 'bi-graph-up' : 'bi-graph-down'" />
                            </div>
                        </div>

                        <div v-if="showFinancialOverview && obligationSummary" class="payment-obligations">
                            <span class="payment-obligations-title">{{$t('current_balance')}}</span>
                            <div class="payment-obligation-item">
                                <div>
                                    <span class="payment-summary-label">{{$t('customer_receivables')}}</span>
                                    <strong>{{formatMoney(obligationSummary.customer_receivables)}}</strong>
                                </div>
                                <small :class="obligationSummary.overdue_customer_receivables > 0 ? 'text-danger' : 'text-muted'">
                                    {{$t('overdue')}}: {{formatMoney(obligationSummary.overdue_customer_receivables)}}
                                </small>
                            </div>
                            <div class="payment-obligation-item">
                                <div>
                                    <span class="payment-summary-label">{{$t('supplier_payables')}}</span>
                                    <strong>{{formatMoney(obligationSummary.supplier_payables)}}</strong>
                                </div>
                                <small :class="obligationSummary.overdue_supplier_payables > 0 ? 'text-danger' : 'text-muted'">
                                    {{$t('overdue')}}: {{formatMoney(obligationSummary.overdue_supplier_payables)}}
                                </small>
                            </div>
                        </div>
                    </section>

                    <div class="d-flex justify-content-end mb-3" v-if="isDetailSupplier && helper.checkAccess('create_payments') != -1">
                        <button @click="create" class="btn btn-sm btn-success">
                            <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                            <span>{{$t('payment')}}</span>
                        </button>
                    </div>

                <div class="page-table-container mb-6 bg-white rounded shadow">

                    <!-- table -->
                    <div class="table-responsive">
                        <table class="table mb-0 table-borderless table-striped small">
                            <thead>
                                <tr class="text-secondary">
                                    <sortable-table-header field="reference" label="reference" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getPayments)" />
                                    <th class="pt-4 pb-3 px-6">{{$t('document')}}</th>
                                    <!-- <th class="pt-4 pb-3 px-6">{{$t('order')}}</th> -->
                                    <th v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('clients')" class="pt-4 pb-3 px-6">{{$t('client')}}</th>
                                    <th v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('supplier_invoices')" class="pt-4 pb-3 px-6">{{$t('supplier_invoice_id')}}</th>
                                    <th v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('pos')" class="pt-4 pb-3 px-6">{{$t('pos')}}</th>
                                    <th v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('suppliers')" class="pt-4 pb-3 px-6">{{$t('supplier')}}</th>
                                    <th class="pt-4 pb-3 px-6">{{$t('category')}}</th>
                                    <sortable-table-header field="amount" label="amount_paid" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getPayments)" />
                                    <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getPayments)" />
                                    <sortable-table-header field="payment_method" label="payment_method" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getPayments)" />
                                    <sortable-table-header field="date" label="date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getPayments)" />
                                    <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                                </tr>
                            </thead>
                            <tbody>
                            <tr v-for="payment in payments" :key="payment.id" :class="{'text-decoration-line-through': payment.status === 'canceled'}">
                                <td class="py-5 px-6"> <router-link :to="`/payments/${payment.id}`"><b>{{payment.reference}}</b></router-link></td>
                                <td class="py-5 px-6">
                                    <router-link v-if="payment?.invoice?.id" :to="`/invoices/${payment?.invoice?.id}`">{{payment?.invoice?.reference}}</router-link>
                                    <router-link v-else-if="payment?.supplierInvoice?.id" :to="`/supplier-invoices/${payment?.supplierInvoice?.id}`">{{payment?.supplierInvoice?.reference}}</router-link>
                                    <router-link v-else-if="payment?.process?.id" to="/receipts">{{payment?.process?.reference}}</router-link>
                                    <router-link v-else-if="payment?.pos?.id" :to="`/pos-transactions/${payment?.pos?.id}`">{{payment?.pos?.reference}}</router-link>
                                </td>
                                <!-- <td class="py-5 px-6">{{payment?.order?.reference}}</td> -->
                                <td v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('clients')" class="py-5 px-6">
                                    <router-link v-if="payment?.client?.id" :to="`/clients/${payment?.client?.id}`">{{payment?.client?.name}}</router-link>
                                </td>
                                <td v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('supplier_invoices')" class="py-5 px-6">
                                    <router-link v-if="payment?.supplierInvoice?.id" :to="`/supplier-invoices/${payment?.supplierInvoice?.id}`">{{payment?.supplierInvoice?.reference}}</router-link>
                                </td>
                                <td v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('pos')" class="py-5 px-6">
                                    <router-link v-if="payment?.pos?.id" :to="`/pos-transactions/${payment?.pos?.id}`">{{payment?.pos?.reference}}</router-link>
                                </td>
                                <td v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('suppliers')" class="py-5 px-6">
                                    <router-link v-if="payment?.supplier?.id" :to="`/suppliers/${payment?.supplier?.id}`">{{payment?.supplier?.name}}</router-link>
                                </td>
                                <td class="py-5 px-6">
                                    <span class="badge bg-light text-dark border">{{getPaymentCategoryName(payment)}}</span>
                                </td>
                                <td class="py-5 px-6">
                                    <span :class="payment.status == 'canceled' ? 'badge bg-secondary' : payment.type == 'revenue' ? 'badge bg-success' : 'badge bg-danger'">
                                        {{payment.type == 'revenue' ? '+' : '-'}} {{formatMoney(payment.amount)}} 
                                    </span>
                                </td>
                                <td class="py-5 px-6"><span>{{$t(payment.status)}}</span></td>
                                <td class="py-5 px-6">{{$t(payment.payment_method)}}</td>
                                <td class="py-5 px-6">{{formatDate(payment.date || payment.created_at)}}</td>
                                <td class="py-5 px-6">
                                    <router-link :to="`/payments/${payment.id}`" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                                        <AppIcon name="bi-eye" class="bi bi-eye" style="font-size:12px" />
                                        {{$t('details')}}
                                    </router-link>
                                    <button  v-if="helper.checkAccess('delete_payments') != -1 && payment.status == 'paid'" @click="deleteRow(payment)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                                        <AppIcon name="bi-x-circle" class="bi bi-x-circle" style="font-size:12px" />
                                        {{$t('cancel_payment')}}
                                    </button>
                                </td>
                            </tr>
                            <tr v-if="payments.length" style="background-color: #f8f9fa;font-weight: bold">
                                <td>{{$t('total')}}:</td>
                                <td></td>
                                <td v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('clients')"></td>
                                <td v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('supplier_invoices')"></td>
                                <td v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('pos')"></td>
                                <td v-if="!hidePaymentAssociations && helper.checkModuleIsEnabled('suppliers')"></td>
                                <td></td>
                                <td class="py-5 px-6">{{formatMoney(totalPayments())}} </td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>   
                            </tr>

                            </tbody>
                        </table>

                        <div v-if="count > payments.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
                            <compact-pagination :pages="pagination" :current-page="page" @change="getPayments" />
                        </div>
                    </div>
                    <!-- table -->
                    <table-loading-bar v-if="loading" class="page-table-state text-center" />
                    <div v-if="loading === false && !payments.length" class="page-table-state text-center py-5 my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
                        <AppIcon name="bi-credit-card" class="bi bi-credit-card text-muted" style="font-size: 3rem;" />
                        <h4 class="mt-3">{{ $t('no_payments_added') }}</h4>
                        <p class="text-muted">{{ $t('start_by_adding_payments') }}</p>
                        <template v-if="helper.checkAccess('create_payments') != -1">
                            <button class="btn btn-outline-primary mt-2" @click="create">
                                {{ $t('add_first_payment') }}
                            </button>
                        </template>
                    </div>

                </div>

                <div class="col-sm-6">
                    {{$t('show')}}
                    <select v-model="per_page" class="form-select mb-3" name="Resulats" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="15">15</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    {{$t('entities')}}
                </div>
                </div>
        </section>


            <!-- create Modal -->
        <add-edit-payment-modal 
            v-if="createModalisOpen" 
            @modalClosed="createModalisOpen = false"
            modalId="create-payment-modal-01" 
            :isModalOpen="createModalisOpen" 
            :data="payment" 
            :hide-payment-associations="hidePaymentAssociations"
            :hide-invoice-detail-fields="!!isDetailSupplier && !!invoice_id"
            action="create"
            @refresh="refresh"
        ></add-edit-payment-modal>
        <!-- create modal -->

  </div>
</template>

<script>
import helper from '../helper'
import moment from 'moment'
// import { jsPDF } from "jspdf";
// import autoTable from 'jspdf-autotable'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import AddEditPaymentModal from '../components/payment/AddEditPaymentModal.vue';
import AutoComplete from '../components/autocomplete/AutoComplete';
import sortableList from '../mixins/sortableList'


export default {
  name: 'Payment',
  persistedFilterState: {
    key: 'payments',
    fields: ['activeFilters', 'clientFilterName', 'dateRangeOption', 'range', 'start', 'end', 'per_page', 'sortField', 'sortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  components : {
    DatePicker,
    AddEditPaymentModal,
    AutoComplete
  },
  data(){
    return {
        search :'',
        payments : [],
        paymentSummary : null,
        obligationSummary : null,
        count : 0,
        page : 1,
        transactions : [],
        deliveries : [],
        payment : {
            id : null,
            reference : `PAYMENT-00`,
            date : '',
            notes : '',
            products : [],
            client_id : null,
            client : {},
            category_id : null,
            payment_method : 'cash',
            status : 'paid',
            rest : 0,
            total : 0,
            amount : 0,
        },
        range: [],
        start : null,
        end : null,
        dateRangeOption: '',
        status : 'all',
        activeFilters: [],
        paymentMethods: [],
        paymentCategories: [],
        availableFilters: [
            { 
                key: 'status', 
                options: [
                    { value: 'paid', label: 'paid' },
                    { value: 'canceled', label: 'canceled' }
                ] 
            },
            { 
                key: 'payment_method', 
                options: [] 
            },
            {
                key: 'category',
                options: []
            },
            {
                key: 'client',
                options: []
            }
        ],
        deliveryProducts : [],
        orderProducts : [],
        totalHtPrice : 0,
        createModal : null,
        editModal : null,
        paymentModalisOpen : false,
        createModalisOpen :false,
        editModalisOpen :false,
        purchasePrice : 0,
        amount : 0,
        loading : false,
        createClientState : false,
        per_page : 25,
        pagination : [],
        hideSupplier : false,
        url : '',
        clientFilterName: ''
    }
  },
  props : [
    'openCreateModal',
    'supplier_id',
    'invoice_id',
    'client_id',
    'user_id',
    'supplier',
    'client',
    'invoice',
    'products',
    'purchase_id',
    'keyword',
    'isDetailSupplier',
    'supplier_invoice_id',
    'paymentable_id',
    'paymentable_type',
    'hidePaymentAssociations',
    'hidePaymentSummary',
    'showFinancialOverview',
    'showFilters'
   ],
  mounted(){
    // Initialize this_month date range
    // this.handleDateRangeChange()
    this.getPayments()
    this.fetchPaymentMethods()
    this.fetchPaymentCategories()
  },
  watch : {
    range(val) {
        if (val && val.length === 2 && val[0] && val[1]) {
            this.start = moment(val[0]).format('YYYY-MM-DD')
            this.end = moment(val[1]).format('YYYY-MM-DD')
            this.getPayments(1)
        } else {
            this.start = null
            this.end = null
            this.getPayments(1)
        }
    },
    per_page(){
        this.getPayments(this.page)
    },
    status(){
        this.getPayments()
    },
    openCreateModal(){
        this.createModalStatus = this.openCreateModal
        this.create()
    },
    keyword() {
        if (this.keyword.length > 2 || this.keyword === '') {
            this.getPayments(1)
        }
    },
  },    
  methods : {
    getPaymentCategoryName(payment) {
        if (payment?.category?.name) return payment.category.name
        if (payment?.invoice?.id || payment?.invoice_id || payment?.paymentable_type === 'invoice') return this.$t('invoice_payments')
        if (payment?.process?.id || payment?.process_id || payment?.paymentable_type === 'process') return this.$t('receipt_payments')
        if (payment?.pos?.id || payment?.pos_id || payment?.paymentable_type === 'pos') return this.$t('pos_payments')
        if (payment?.supplierInvoice?.id || payment?.supplier_invoice_id || payment?.paymentable_type === 'supplier_invoice') return this.$t('supplier_invoice_payments')
        if (payment?.purchase?.id || payment?.purchase_id || payment?.paymentable_type === 'purchase') return this.$t('purchase_payments')
        if (payment?.paymentable_type === 'subscription') return this.$t('subscription_payments')
        if (payment?.paymentable_type === 'project') return this.$t('project_payments')
        if (payment?.supplier?.id || payment?.supplier_id) return this.$t('supplier_payments')
        if (payment?.client?.id || payment?.client_id) return this.$t('client_payments')
        if (payment?.type === 'expense') return this.$t('other_expenses')
        if (payment?.type === 'revenue') return this.$t('other_income')
        return this.$t('uncategorized')
    },
    async create(){

        if(this.supplier){
            this.payment.supplier = this.supplier
        }

        if(this.supplier_id){
            this.payment.supplier_id = this.supplier_id
        }

        if(this.purchase_id){
            this.payment.purchase_id = this.purchase_id
        }

        if(this.client_id){
            this.payment.client_id = this.client_id
        }

        if(this.client){
            this.payment.client = this.client
        }

        if(this.invoice_id){
            this.payment.invoice_id = this.invoice_id
            if (this.isDetailSupplier) {
                this.payment.hideTypeToggle = true;
            }
        }

        if(this.invoice){
            this.payment.invoice = this.invoice
        }

        if(this.paymentable_id){
            this.payment.paymentable_id = this.paymentable_id
        }

        if(this.paymentable_type){
            this.payment.paymentable_type = this.paymentable_type
        }

        if(this.products){
            for(let i = 0 ; i < this.products.length ; i++){
                this.products[i].label = this.products[i].product_label 
            }
            this.payment.products = this.products
        }

        this.createModalisOpen = true;

        this.payment.date = moment().format('YYYY-MM-DD')
    },
     refresh(){
        this.getPayments()
    },
    viewRow(row){
        this.clearForm()
        this.payment = row 
        this.paymentModalisOpen = true;
    },
    previewRow(){
        this.paymentModalisOpen = true;
    },
    async deleteRow(row){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            confirmButtonColor: '#d33',
            confirmButtonText: this.$t('yes_cancel_it')
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customPatch(`${process.env.VUE_APP_API}/transactions/${row.id}`,{status :'canceled'})
                    await this.getPayments(this.page)
                }
            })
    },
    clearForm(){
        this.payment = {
            id : null,
            reference : '',
            date : '',
            products : [],
            category_id : null,
            payment_method : 'cash',
            status : 'paid',
            rest : 0,
            total : 0,
            amount : 0,
        }

    },
    async getPayments(page){
        try{
            this.loading = true
            this.page = page ? page : 1

            // Extract filters
            const statusFilter = this.activeFilters.find(f => f.key === 'status');
            const status = statusFilter ? statusFilter.value : this.status;
            const paymentMethodFilter = this.activeFilters.find(f => f.key === 'payment_method');
            const categoryFilter = this.activeFilters.find(f => f.key === 'category');
            const clientFilter = this.activeFilters.find(f => f.key === 'client');

            // Build query params
            const params = new URLSearchParams();
            params.append('page', this.page);
            params.append('per_page', this.per_page);
            params.append('status', status);
            params.append('date_basis', 'payment_date');
            if (this.showFinancialOverview) params.append('include_obligations', 'true');
            this.appendSortParams(params);

            if (this.start) {
                params.append('start', this.start);
                params.append('end', this.end);
            }

            if (paymentMethodFilter && paymentMethodFilter.value) params.append('payment_method', paymentMethodFilter.value);
            if (categoryFilter && categoryFilter.value) params.append('category_id', categoryFilter.value);
            if (clientFilter && clientFilter.value) params.append('client_id', clientFilter.value);

            // Add optional filters if they exist
            if (this.supplier_id) params.append('supplier_id', this.supplier_id);
            if (this.purchase_id) params.append('purchase_id', this.purchase_id);
            if (this.invoice_id) params.append('invoice_id', this.invoice_id);
            if (this.client_id) params.append('client_id', this.client_id);
            if (this.supplier_invoice_id) params.append('supplier_invoice_id', this.supplier_invoice_id);
            if (this.user_id) params.append('user_id', this.user_id);
            if (this.paymentable_id) params.append('paymentable_id', this.paymentable_id);
            if (this.paymentable_type) params.append('paymentable_type', this.paymentable_type);
            if (this.keyword && this.keyword.length > 2) params.append('keyword', this.keyword);

            let url = `${process.env.VUE_APP_API}/transactions?${params.toString()}`;

            const response = await helper.customGet(url)
            this.payments = response.data.data
            this.paymentSummary = response.data.summary || null
            this.obligationSummary = response.data.obligations || null
            this.count = response.data.count
            if(this.per_page != response.data.per_page){
                this.per_page = response.data.per_page
            }
            //pagination
            this.pagination = []
            let MaxPagination = Math.ceil(this.count / this.per_page )
            for(let i = 1 ; i <= MaxPagination ;i++){
                this.pagination.push(i)
            }

            this.loading = false
        }catch(error){
            this.loading = false
        }

    },
    totalPayments() {
        if (this.paymentSummary) return Number(this.paymentSummary.net_cash_flow) || 0;
        return this.payments.reduce((total, payment) => {
            if (payment.status === 'paid') {
                if (payment.type === 'revenue') {
                    return total + payment.amount;
                } else {
                    return total - payment.amount;
                }
            }
            return total;
        }, 0);
    },
    totalEarnings() {
        if (this.paymentSummary) return Number(this.paymentSummary.total_income) || 0;
        return this.payments.reduce((total, payment) => {
            if (payment.status === 'paid' && payment.type === 'revenue') {
                return total + payment.amount;
            }
            return total;
        }, 0);
    },
    totalSpending() {
        if (this.paymentSummary) return Number(this.paymentSummary.total_expense) || 0;
        return this.payments.reduce((total, payment) => {
            if (payment.status === 'paid' && payment.type === 'expense') {
                return total + payment.amount;
            }
            return total;
        }, 0);
    },
    netCashflow() {
        if (this.paymentSummary) return Number(this.paymentSummary.net_cash_flow) || 0;
        return this.totalEarnings() - this.totalSpending();
    },
    // generatePDF() {
    //     const doc = new jsPDF();

    //     const data = []
    //     for(let i = 0 ; i < this.payments.length ; i++){
    //         const payment = this.payments[i]
            
    //         data.push([
    //             payment.reference,
    //             `${this.formatMoney(this.calculateItemPrice(payment))}`,
    //             `${this.formatMoney(payment.amount)}`,
    //             this.$t(payment.status),
    //             payment.purchase && payment.purchase.reference ? payment.purchase.reference : '-',
    //             this.$t(payment.payment_method),
    //             payment.purchase && payment.purchase &&  payment.purchase.supplier && payment.purchase.supplier.name ? payment.purchase.supplier.name : '-',
    //             this.formatDate(payment.date)
    //         ])
    //     }
    //     doc.text("Réglements", 15, 10);
    //     autoTable(doc,
    //     {
    //         head: [["Id", "Total(ttc)", "Total donnée", "Status", "Mode de paiment", "Achat", "Fournisseur","Date"]],
    //         body: data
    //     })
    //     doc.save('reglements.pdf')
    // },
    // generateCsv(){
    //     const data = []
    //     for(let i = 0 ; i < this.orders.length ; i++){
    //         const order = this.orders[i]
    //         data.push({
    //             "Id" : order.id,
    //             "Total(ttc)" : `${this.formatMoney(this.calculateItemPrice(order))}` ,
    //             "Total Donnée" : `${this.formatMoney(order.amount)}`,
    //             "Rest" : `${this.formatMoney(order.total - order.amount)}`,
    //             "Status" : this.$t(order.status),
    //             "Mode de paiment" : this.$t(order.payment_method),
    //             "Client" : order.client && order.client.name ? order.client.name : '-',
    //             "Date" : this.formatDate(order.date)
    //         })
    //     }

    //     return data
    // },
    //helper functions
    formatDate(date) { return helper.formatDate(date) },
    formatMoney(number) { return helper.formatMoney(number);},
    formatReference(number,id) { return helper.formatReference(number,id);},
    async fetchPaymentMethods() {
        try {
            const response = await helper.customGet(`${process.env.VUE_APP_API}/payment-methods?per_page=100`)
            this.paymentMethods = response.data.data || []
            // Update the payment_method filter options dynamically
            const pmFilter = this.availableFilters.find(f => f.key === 'payment_method')
            if (pmFilter) {
                pmFilter.options = this.paymentMethods.map(pm => ({
                    value: pm.slug,
                    label: pm.name
                }))
            }
        } catch (error) {
            alert(error)
        }
    },
    async fetchPaymentCategories() {
        try {
            const [incomeResponse, expenseResponse] = await Promise.all([
                helper.customGet(`${process.env.VUE_APP_API}/transaction-categories?per_page=100&sort_field=name&sort_type=asc&type=revenue`),
                helper.customGet(`${process.env.VUE_APP_API}/transaction-categories?per_page=100&sort_field=name&sort_type=asc&type=expense`)
            ])
            const incomeCategories = (incomeResponse.data.data || []).map(category => ({...category, type: 'revenue'}))
            const expenseCategories = (expenseResponse.data.data || []).map(category => ({...category, type: 'expense'}))
            this.paymentCategories = [...incomeCategories, ...expenseCategories]
            const categoryFilter = this.availableFilters.find(f => f.key === 'category')
            if (categoryFilter) {
                categoryFilter.options = this.paymentCategories.map(category => ({
                    value: category.id,
                    label: `${this.$t(category.type)} · ${category.name}`
                }))
            }
        } catch (error) {
            this.paymentCategories = []
        }
    },
    // Handle date range selection
    handleDateRangeChange() {
      const option = this.dateRangeOption
      if (!option || option === 'custom') {
        if (option === 'custom') return
        this.range = []
        this.start = null
        this.end = null
        this.getPayments()
        return
      }

      const today = moment()
      let start, end

      switch(option) {
        case 'today':
          start = today.clone().startOf('day')
          end = today.clone().endOf('day')
          break
        case 'yesterday':
          start = today.clone().subtract(1, 'days').startOf('day')
          end = today.clone().subtract(1, 'days').endOf('day')
          break
        case 'this_week':
          start = today.clone().startOf('isoWeek')
          end = today.clone().endOf('isoWeek')
          break
        case 'last_week':
          start = today.clone().subtract(1, 'weeks').startOf('isoWeek')
          end = today.clone().subtract(1, 'weeks').endOf('isoWeek')
          break
        case 'this_month':
          start = today.clone().startOf('month')
          end = today.clone().endOf('month')
          break
        default:
          return
      }

      this.range = [start.toDate(), end.toDate()]
      this.start = start.format('YYYY-MM-DD')
      this.end = end.format('YYYY-MM-DD')
      this.getPayments()
    },
    addFilter(filter) {
        if (!this.isFilterActive(filter.key)) {
            if (filter.key === 'client') {
                this.clientFilterName = '';
                this.activeFilters.push({
                    key: filter.key,
                    value: null
                });
            } else {
                const defaultVal = filter.options && filter.options.length > 0 ? filter.options[0].value : '';
                this.activeFilters.push({ 
                    key: filter.key, 
                    value: defaultVal 
                });
            }
        }
    },
    removeFilter(index) {
        const filter = this.activeFilters[index];
        if (filter && filter.key === 'client') {
            this.clientFilterName = '';
        }
        this.activeFilters.splice(index, 1);
    },
    isFilterActive(key) {
        return this.activeFilters.some(f => f.key === key);
    },
    getFilterOptions(key) {
        const filter = this.availableFilters.find(f => f.key === key);
        return filter ? filter.options : [];
    },
    applyFilters() {
        const statusFilter = this.activeFilters.find(f => f.key === 'status');
        if (statusFilter) {
            this.status = statusFilter.value;
        } else {
            this.status = 'all';
        }
        this.getPayments();
    },
    clearAllFilters() {
        this.activeFilters = [];
        this.status = 'all';
        this.clientFilterName = '';
        this.getPayments();
    },
    emitAutoCompleteClientFilter(data) {
        const clientFilter = this.activeFilters.find(f => f.key === 'client');
        if (data.clear_input) {
            if (clientFilter) {
                clientFilter.value = null;
            }
            this.clientFilterName = '';
            return;
        }
        if (data.result && clientFilter) {
            clientFilter.value = data.result.id;
            this.clientFilterName = data.result.name;
        }
    }
  },
  computed : {
    selectableFilters() {
        return this.availableFilters.filter(f => {
            if (f.key === 'client' && !helper.checkModuleIsEnabled('clients')) {
                return false;
            }
            if (f.key === 'category' && f.options.length === 0) {
                return false;
            }
            return !this.isFilterActive(f.key);
        });
    },
    isFiltered() {
      return (this.activeFilters.length > 0) ||
             (this.dateRangeOption && this.dateRangeOption !== '') ||
             (this.keyword && this.keyword.length > 0) ||
             (this.range && this.range.length > 0);
    },
    role(){ return this.$store.getters.ROLE },
    helper(){ return helper },
    permissions(){ return this.$store.getters.PERMISSIONS },
    business(){ return this.$store.getters.BUSINESS},
    total(){ return this.payment.products.length ? this.payment.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
    sub_total(){ return this.payment.products.length ? this.payment.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0}
  }
}
</script>

<style scoped>
    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }

    .payment-summary-panel {
        overflow: hidden;
        background: #fff;
        border: 1px solid #e7ebf0;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(17, 24, 39, 0.06);
    }

    .payment-summary-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 16px;
        border-bottom: 1px solid #edf0f3;
    }

    .payment-summary-title {
        font-size: 13px;
        font-weight: 700;
        color: #374151;
    }

    .payment-summary-scope {
        font-size: 12px;
        color: #6b7280;
    }

    .payment-summary-metrics {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .payment-summary-metric {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-width: 0;
        gap: 12px;
        padding: 14px 18px;
    }

    .payment-summary-metric + .payment-summary-metric {
        border-inline-start: 1px solid #edf0f3;
    }

    .payment-summary-label {
        display: block;
        margin-bottom: 3px;
        color: #6b7280;
        font-size: 12px;
        font-weight: 600;
    }

    .payment-summary-value {
        display: block;
        overflow: hidden;
        color: #1f2937;
        font-size: clamp(18px, 1.5vw, 23px);
        line-height: 1.2;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .payment-summary-icon {
        flex: 0 0 auto;
        font-size: 24px;
    }

    .payment-summary-metric--income .payment-summary-icon { color: #16a34a; }
    .payment-summary-metric--expense .payment-summary-icon { color: #dc3545; }
    .payment-summary-metric--net .payment-summary-icon { color: #0d6efd; }

    .payment-obligations {
        display: grid;
        grid-template-columns: auto repeat(2, minmax(0, 1fr));
        align-items: center;
        gap: 0;
        padding: 0 16px;
        background: #f8fafc;
        border-top: 1px solid #edf0f3;
    }

    .payment-obligations-title {
        padding-inline-end: 18px;
        color: #6b7280;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    .payment-obligation-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        min-width: 0;
        padding: 10px 18px;
        border-inline-start: 1px solid #e5e7eb;
    }

    .payment-obligation-item strong {
        font-size: 14px;
        white-space: nowrap;
    }

    .payment-obligation-item small {
        white-space: nowrap;
    }

    @media (max-width: 991.98px) {
        .payment-obligations {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            padding: 0;
        }

        .payment-obligations-title {
            grid-column: 1 / -1;
            padding: 8px 16px 0;
        }

        .payment-obligation-item:first-of-type {
            border-inline-start: 0;
        }
    }

    @media (max-width: 767.98px) {
        .payment-summary-header,
        .payment-obligation-item {
            align-items: flex-start;
            flex-direction: column;
        }

        .payment-summary-metrics,
        .payment-obligations {
            grid-template-columns: 1fr;
        }

        .payment-summary-metric + .payment-summary-metric,
        .payment-obligation-item,
        .payment-obligation-item:first-of-type {
            border-inline-start: 0;
            border-top: 1px solid #edf0f3;
        }

        .payment-obligations-title {
            grid-column: auto;
        }
    }
</style>
